"use client"

import { useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Plus, Trash2, MoveUp, MoveDown } from 'lucide-react';
import DealService from '@/api/deal.service';

const stageSchema = z.object({
  name: z.string().min(1, 'Stage name is required'),
  probability: z.string().refine(
    (val) => !isNaN(Number(val)) && Number(val) >= 0 && Number(val) <= 100,
    { message: 'Probability must be a number between 0 and 100' }
  ),
});

const formSchema = z.object({
  name: z.string().min(1, 'Pipeline name is required'),
  stages: z.array(stageSchema).min(2, 'At least 2 stages are required'),
});

interface CreatePipelineFormProps {
  businessId: number;
  onSuccess: () => void;
}

export default function CreatePipelineForm({ businessId, onSuccess }: CreatePipelineFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: 'Sales Pipeline',
      stages: [
        { name: 'Lead', probability: '10' },
        { name: 'Proposal', probability: '50' },
        { name: 'Negotiation', probability: '80' },
        { name: 'Won', probability: '100' },
      ],
    },
  });
  
  const { fields, append, remove, move } = useFieldArray({
    control: form.control,
    name: 'stages',
  });
  
  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsLoading(true);
    try {
      const pipelineData = {
        name: values.name,
        stages: values.stages.map((stage, index) => ({
          name: stage.name,
          probability: parseInt(stage.probability),
          order: index,
        })),
      };
      
      const response = await DealService.createPipeline(businessId, pipelineData);
      console.log("createPipeline response.data", response.data);
      if (response.data.result === 'success') {
        toast({
          title: "Success",
          description: "Pipeline created successfully!",
        });
        onSuccess();
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to create pipeline.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error creating pipeline:", error);
      toast({
        title: "Error",
        description: "An error occurred while creating the pipeline.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const addStage = () => {
    append({ name: '', probability: '0' });
  };
  
  const moveStageUp = (index: number) => {
    if (index > 0) {
      move(index, index - 1);
    }
  };
  
  const moveStageDown = (index: number) => {
    if (index < fields.length - 1) {
      move(index, index + 1);
    }
  };
  
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Pipeline Name</FormLabel>
              <FormControl>
                <Input placeholder="Sales Pipeline" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <FormLabel>Pipeline Stages</FormLabel>
            <Button 
              type="button" 
              variant="outline" 
              size="sm" 
              onClick={addStage}
              className="gap-1"
            >
              <Plus className="w-4 h-4" />
              Add Stage
            </Button>
          </div>
          
          {fields.map((field: { id: string; name: string; probability: string }, index: number) => (
            <div 
              key={field.id} 
              className="flex items-start gap-2 p-3 border rounded-md relative"
            >
              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name={`stages.${index}.name`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Stage Name</FormLabel>
                      <FormControl>
                        <Input placeholder="E.g., Lead, Negotiation" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name={`stages.${index}.probability`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Probability (%)</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          min="0" 
                          max="100" 
                          placeholder="50"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              
              <div className="flex flex-col gap-1 mt-8">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => moveStageUp(index)}
                  disabled={index === 0}
                  className="h-8 w-8 p-0"
                >
                  <MoveUp className="w-4 h-4" />
                </Button>
                
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => moveStageDown(index)}
                  disabled={index === fields.length - 1}
                  className="h-8 w-8 p-0"
                >
                  <MoveDown className="w-4 h-4" />
                </Button>
              </div>
              
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => remove(index)}
                disabled={fields.length <= 2}
                className="h-8 w-8 p-0 text-destructive hover:text-destructive/80 hover:bg-destructive/10 absolute top-2 right-2"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          
          {form.formState.errors.stages && !Array.isArray(form.formState.errors.stages) && (
            <p className="text-sm font-medium text-destructive">{form.formState.errors.stages.message}</p>
          )}
        </div>
        
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onSuccess}>
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create Pipeline
          </Button>
        </div>
      </form>
    </Form>
  );
} 